// PRD §3.3 + §3.6 + Phase 6 M10.2 — shared request/response schemas for the
// /api/v1/events and /api/v1/events/sync endpoints.
//
// Why these live in `shared/`: the mobile sync queue and the backend route
// must agree on the wire shape down to the field name. Phase 5 smoke testing
// (2026-05-11) caught a `data` vs `data_json` drift that had been latent
// since M5. Centralising the schema here is the structural fix.
import { z } from 'zod';
import { EVENT_TYPES } from './event.zod.js';
export const EventTypeEnum = z.enum(EVENT_TYPES);
export const EventInputSchema = z
    .object({
    animal_id: z.string().uuid(),
    type: EventTypeEnum,
    data_json: z.unknown(),
    timestamp: z
        .string()
        .datetime({ offset: true })
        .optional()
        .describe('ISO 8601 with offset; defaults to server now() when omitted'),
    gps_lat: z.number().min(-90).max(90).nullable().optional(),
    gps_lng: z.number().min(-180).max(180).nullable().optional(),
    vet_signature: z.string().min(1).max(512).nullable().optional(),
    client_event_id: z.string().uuid().nullable().optional(),
    // Phase 13 M8 — when a vet records a multi-animal act offline (one
    // vacuna applied to 30 cows), every per-animal event carries the same
    // `batch_id` so the server can reconstruct the act from the sync. The
    // batch row itself is created server-side from the first event that
    // arrives (current behaviour preserves the legacy in-memory adapter; a
    // future iteration will materialise an `EventBatch`).
    batch_id: z.string().uuid().nullable().optional(),
    // Phase 25 iter-23 (Lote J, C2) — device wall-clock at the moment the
    // user actually recorded the event. `timestamp` may be edited by the
    // user (e.g. a vet logging "yesterday's" vaccine), so this is the
    // tie-breaker the server uses when two devices submit conflicting
    // events for the same animal/day. Optional for backwards compat.
    client_created_at: z.string().datetime({ offset: true }).nullable().optional(),
})
    .strict();
// Sync inputs MUST carry their own client_event_id — idempotency key for
// offline replays (PRD §3.6).
export const SyncEventInputSchema = EventInputSchema.extend({
    client_event_id: z.string().uuid(),
    timestamp: z.string().datetime({ offset: true }),
});
export const SyncEventsBodySchema = z
    .object({
    events: z.array(SyncEventInputSchema).min(1).max(500),
})
    .strict();
export const StoredEventResponseSchema = z.object({
    id: z.string().uuid(),
    animal_id: z.string().uuid(),
    type: EventTypeEnum,
    data_json: z.unknown(),
    timestamp: z.string().datetime({ offset: true }),
    gps_lat: z.number().nullable(),
    gps_lng: z.number().nullable(),
    user_id: z.string().uuid(),
    prev_hash: z.string().nullable(),
    hash: z.string(),
    vet_signature: z.string().nullable(),
    client_event_id: z.string().uuid().nullable(),
    batch_id: z.string().uuid().nullable(),
    created_at: z.string().datetime({ offset: true }),
});
// Phase 25 iter-23 (Lote J, C2) — every per-event entry in the sync
// response is now either a stored row OR a rejection record. We can't use
// `discriminatedUnion` cleanly because the stored shape is shared with
// `StoredEventResponse`; instead we keep all fields nullable and add a
// `rejected` flag the client can branch on.
export const SyncEventResultSchema = StoredEventResponseSchema.partial({
    // The stored fields become optional so a rejection record can omit
    // them. Mobile still must check `rejected` before consuming.
    id: true,
    prev_hash: true,
    hash: true,
    user_id: true,
    created_at: true,
    data_json: true,
    gps_lat: true,
    gps_lng: true,
    vet_signature: true,
    batch_id: true,
    timestamp: true,
    type: true,
    animal_id: true,
}).extend({
    idempotent_replay: z.boolean(),
    rejected: z.boolean().default(false),
    rejected_reason: z.string().nullable().default(null),
});
export const SyncEventsResponseSchema = z.object({
    events: z.array(SyncEventResultSchema),
});
// GET /api/v1/animals/:id/events — paginated timeline. Same bounded-list
// pattern as AnimalListResponse: defaults to 200, max 500, cursor is the
// id of the last event from the previous page. Ordering is chronological
// ASC so the timeline reads naturally top-to-bottom. Optional `type`
// filter mirrors the PRD §3.3 example `?type=VACCINE`.
export const EVENT_LIST_DEFAULT_LIMIT = 200;
export const EVENT_LIST_MAX_LIMIT = 500;
export const EventListQuerySchema = z
    .object({
    type: EventTypeEnum.optional(),
    limit: z.coerce.number().int().min(1).max(EVENT_LIST_MAX_LIMIT).optional(),
    cursor: z.string().uuid().optional(),
})
    .strict();
export const EventListResponseSchema = z.object({
    events: z.array(StoredEventResponseSchema),
    next_cursor: z.string().uuid().nullable().default(null),
});
//# sourceMappingURL=event-input.js.map